import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmblaCarousel from "@tCentro/Components/EmblaCarousel";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    title = "title",
    multilineTextFieldValue = "multilineTextFieldValue",
    nodeId = "nodeId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.title]: string;
        [fields.multilineTextFieldValue]: string;
        [fields.nodeId]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];
    const blogNodeId = ownProps.fields[fields.nodeId];
    const newsPages = getDataView(state.data.pages, { parentNodeId: nodeId });
    const blogPages = getDataView(state.data.pages, { parentNodeId: blogNodeId });
    const mergedMap = new Map();
    (newsPages.value ?? []).forEach(page => mergedMap.set(page.nodeId, { ...page, __source: "news" }));
    (blogPages.value ?? []).forEach(page => mergedMap.set(page.nodeId, { ...page, __source: "blog" })); // overwrites if duplicate, blog wins

    const mergedPagesValue = newsPages.value || blogPages.value ? Array.from(mergedMap.values()) : undefined;

    const pages = {
        ...newsPages,
        value: mergedPagesValue,
        isLoading: newsPages.isLoading || blogPages.isLoading,
    };

    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: nodeId,
        location: getLocation(state),
        pages,
        links,
        newsPagesLoaded: !!newsPages.value, // new
        newsPagesLoading: !!newsPages.isLoading, // new
        blogPagesLoaded: !!blogPages.value, // new
        blogPagesLoading: !!blogPages.isLoading,
        parameter: state.pages.newsList.getNewsPagesParameters[nodeId] ?? {
            parentNodeId: nodeId,
        },
        viewAll: getPageLinkByPageType(state, "NewsListPage")?.url,
        blogNodeId,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasCategoryContext;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    newsParentWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    categoryArticleLink?: LinkPresentationProps;
    browseAllLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    header?: TextFieldPresentationProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    articleImage?: InjectableCss;
    articleTitleWrapper?: InjectableCss;
    multilineTextFieldValue?: InjectableCss;
    titleWrapper?: InjectableCss;
    browseAllWrapper?: InjectableCss;
    imgContainer?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        size: 20,
        css: css`
            text-align: center;
        `,
    },
    newsParentWrapper: {
        css: css`
            max-width: 290px;
        `,
    },
    articleImage: {
        css: css`
            width: 100%;
            height: auto;
            aspect-ratio: 1.78/1;
            object-fit: contain;
        `,
    },
    articleTitleWrapper: {
        css: css`
            padding: 20px 30px 30px;
        `,
    },
    newsWrapper: {
        css: css`
            background-color: ${COLORS.background.grayShade};
            height: 100%;
            .news-wrapper {
                display: flex;
            }
            .news-content {
                width: 70%;
                padding-left: 10px;
                h3 {
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.base};
                    a {
                        & :focus {
                            outline: none;
                        }
                        span {
                            color: ${getProp("theme.typography.p.color")};
                        }
                    }
                }

                h5 {
                    font-size: ${TYPOGRAPHY.size.tiny};
                    color: #0b70b7;
                    margin-bottom: 5px;
                }

                p {
                    display: none;
                }
            }

            .newz-img {
                width: 5.625rem;
                height: 60px;
                .img-wrapper {
                    img {
                        height: inherit;
                    }
                }
            }
        `,
    },
    categoryArticleLink: {
        css: css`
            font-size: ${TYPOGRAPHY.size.tiny};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            > span {
                text-transform: uppercase;
            }
            p {
                color: ${getProp("theme.typography.p.color")};
            }
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            line-height: 20px;
            max-height: 72px;
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            p {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: 24px;
                color: ${getProp("theme.typography.p.color")};
                text-transform: capitalize;
            }
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: start;
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    `,
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    css`
                        justify-content: center;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    wrapper: {},
    browseAllWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: 40px;
            margin-bottom: 116px;
        `,
    },
    browseAllLink: {
        css: css`
            display: inline-flex;
            justify-content: center;
            align-items: center;
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 24px;
            color: ${COLORS.secondary.base};
            padding: 12px 24px;
            width: 238px;
            height: 48px;
            border: 2px solid ${COLORS.secondary.base};
            border-radius: 24px;

            span {
                color: ${COLORS.secondary.base} !important;
            }
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            position: relative;
            padding-bottom: 30px;
        `,
    },
    carouselSlide: {
        css: css`
            flex: 0 0 85%;
            padding: 0;
            position: relative;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: left;
            position: relative;
            padding: 10px 3px 0;
        `,
    },
    multilineTextFieldValue: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    titleWrapper: {
        css: css`
            text-align: center;
            margin: 0 auto 50px !important;
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        if (!this.props.newsPagesLoaded && this.props.parentNodeId && !this.props.newsPagesLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        if (!this.props.blogPagesLoaded && this.props.blogNodeId && !this.props.blogPagesLoading) {
            this.props.loadPagesForParent({ parentNodeId: this.props.blogNodeId });
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.newsPagesLoaded && this.props.parentNodeId && !this.props.newsPagesLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        if (!this.props.blogPagesLoaded && this.props.blogNodeId && !this.props.blogPagesLoading) {
            this.props.loadPagesForParent({ parentNodeId: this.props.blogNodeId });
        }
        this.checkQueryString();
    }

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize, title, multilineTextFieldValue },
            parameter,
            viewAll,
        } = this.props;

        if (!pages) {
            return null;
        }

        // const page = parameter.page ?? 1;
        // const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(
            pages,
            // Primary: blog articles first (0 = blog, 1 = news)
            o => (o.__source === "blog" ? 0 : 1),
            // Secondary: newest first within each group
            o => -new Date(o.fields["publishDate"]).getTime(),
        );

        const categoryArticlePages = sortedAndPagedPages.filter((page: PageProps) => {
            return (
                page.fields["categoryName"] &&
                this.props.category?.name &&
                page.fields["categoryName"]
                    .split(",")
                    .map((n: string) => n.trim())
                    .includes(this.props.category?.name)
            );
        });

        if (categoryArticlePages?.length === 0) {
            return null;
        }



        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography variant="h2" {...styles.titleText}>
                        {" "}
                        {title}
                    </Typography>
                    <Typography variant="p" {...styles.multilineTextFieldValue}>
                        {multilineTextFieldValue}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.listWrapper} className="detailList">
                    {categoryArticlePages.map((page: PageProps) => (
                        <StyledWrapper {...styles.newsParentWrapper} key={page.id}>
                            <StyledWrapper {...styles.newsWrapper} key={page.id} className="newsWrapper">
                                <StyledWrapper>
                                    {page.fields["image"] && (
                                        <Img
                                            {...styles.articleImage}
                                            src={page.fields["image"]}
                                            altText={page.fields["title"]}
                                        />
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.articleTitleWrapper}>
                                    {page.fields["title"] && (
                                        <StyledWrapper>
                                            <Link href={links[page.nodeId]?.url} {...styles.categoryArticleLink}>
                                                {page.fields["title"]}
                                            </Link>
                                        </StyledWrapper>
                                    )}
                                    {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                        <StyledWrapper {...styles.summaryWrapper}>
                                            {parse(
                                                transformContentHtml(
                                                    page.fields["newsSummary"] ||
                                                        truncate(page.fields["newsContent"], { length: 500 }),
                                                ),
                                                parserOptions,
                                            )}
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
                {/* <StyledWrapper {...styles.browseAllWrapper}>
                    <Link href={viewAll} {...styles.browseAllLink}>
                        {translate("Browse All")}
                    </Link>
                </StyledWrapper> */}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(withCategory(NewsListView))),
    definition: {
        group: "News List",
        fieldDefinitions: [
            {
                displayName: "CEN Category Articles",
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "Translatable",
                min: 1,
                max: 20,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.multilineTextFieldValue,
                displayName: "MultilineText",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.nodeId,
                displayName: "Node Id",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
